import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSEOScript } from '@/hooks/useSEOScript';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function AboutPage() {
  usePageMeta(
    '关于我们',
    '乐鱼体育新闻资讯站品牌介绍 — 国内领先的综合性体育资讯平台，200+专业编辑，7x24小时实时更新，日访问量超500万。了解乐鱼体育的品牌故事、平台信誉与联系方式。'
  );

  useSEOScript({
    type: 'Organization',
    data: {
      name: '乐鱼体育',
      url: 'https://www.leyusports.com',
      description: '乐鱼体育新闻资讯站，国内领先的综合性体育资讯平台。',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '400-888-9999',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '体育中心路88号乐鱼大厦',
        addressLocality: '北京',
        addressRegion: '朝阳区',
        addressCountry: 'CN',
      },
    },
  });

  useSEOScript({
    type: 'FAQPage',
    data: {
      mainEntity: [
        {
          '@type': 'Question',
          name: '乐鱼体育新闻资讯站提供哪些体育项目的资讯？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '乐鱼体育新闻资讯站涵盖足球、篮球、综合格斗、网球、电竞、赛车等热门体育项目的全方位资讯服务。',
          },
        },
        {
          '@type': 'Question',
          name: '乐鱼体育的资讯更新频率如何？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '乐鱼体育拥有200多名专业编辑记者团队，7x24小时全天候实时更新体育资讯，确保您第一时间获取最新赛事动态。',
          },
        },
        {
          '@type': 'Question',
          name: '如何联系乐鱼体育？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '您可以通过电子邮箱 contact@leyusports.com 或拨打客服电话 400-888-9999 联系我们。公司地址位于北京市朝阳区体育中心路88号乐鱼大厦。',
          },
        },
      ],
    },
  });

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="w-full bg-background-100/50 border-b border-background-200/70">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
          <div className="w-full px-4 md:px-6 pb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900">关于我们</h1>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-5xl mx-auto space-y-16">

            <section>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className="ri-building-4-line text-xl text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900">品牌介绍</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="text-foreground-700 leading-relaxed mb-4">
                    <strong className="text-foreground-900">乐鱼体育新闻资讯站</strong>成立于2020年，是国内领先的综合性体育资讯平台。我们致力于为体育爱好者提供全方位、高质量的体育新闻资讯服务，涵盖足球、篮球、综合格斗、网球、电竞、赛车等热门体育项目。
                  </p>
                  <p className="text-foreground-700 leading-relaxed mb-4">
                    乐鱼体育拥有一支由200多名专业体育编辑与记者组成的团队，遍布全球各大体育赛事现场，为您带来<strong className="text-foreground-900">第一手的赛事报道</strong>和<strong className="text-foreground-900">深度分析</strong>。
                  </p>
                  <p className="text-foreground-700 leading-relaxed">
                    我们秉持<strong className="text-foreground-900">"专业、客观、快速、全面"</strong>的理念，7x24小时不间断更新，确保每一位体育爱好者都能在第一时间获取最新、最准确的体育资讯。
                  </p>
                </div>
                <div className="overflow-hidden rounded-xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20bright%20newsroom%20office%20with%20multiple%20screens%20displaying%20sports%20news%2C%20professional%20editorial%20team%20environment%2C%20warm%20natural%20lighting%20through%20large%20windows%2C%20clean%20contemporary%20interior%20design%2C%20collaborative%20workspace%20atmosphere&width=800&height=500&seq=about-brand-1&orientation=landscape"
                    alt="乐鱼体育编辑团队"
                    className="w-full aspect-[8/5] object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100">
                  <i className="ri-shield-star-line text-xl text-accent-600 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900">平台信誉</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {[
                  { icon: 'ri-user-star-line', value: '500万+', label: '日均访问量' },
                  { icon: 'ri-group-line', value: '2000万+', label: '月活跃用户' },
                  { icon: 'ri-global-line', value: '50+', label: '官方合作机构' },
                  { icon: 'ri-team-line', value: '200+', label: '专业编辑记者' },
                  { icon: 'ri-award-line', value: '连续三年', label: '最佳体育资讯平台' },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col items-center text-center p-5 rounded-xl bg-background-50 border border-background-200/70"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mb-3">
                      <i className={`${item.icon} text-xl text-accent-600 w-5 h-5 flex items-center justify-center`}></i>
                    </div>
                    <span className="text-2xl font-bold text-foreground-900 mb-1">{item.value}</span>
                    <span className="text-sm text-foreground-500">{item.label}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100">
                  <i className="ri-customer-service-2-line text-xl text-secondary-600 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900">联系我们</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-mail-line text-lg text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">contact@leyusports.com</p>
                    <p className="text-sm text-foreground-600">editor@leyusports.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                    <i className="ri-phone-line text-lg text-accent-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">联系电话</h3>
                    <p className="text-sm text-foreground-600">400-888-9999</p>
                    <p className="text-sm text-foreground-600">010-8888-9999</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-secondary-100 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg text-secondary-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                    <p className="text-sm text-foreground-600">北京市朝阳区体育中心路88号</p>
                    <p className="text-sm text-foreground-600">乐鱼大厦 15-18层</p>
                  </div>
                </div>
              </div>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}